// © 2025 Kaitlyn Troxell
import React, { useMemo, useState } from 'react';

function EventListView({ events, contractors, tasks, onEventClick, isAdmin }) {
  const [groupBy, setGroupBy] = useState('date'); // 'date', 'contractor', or 'task'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Sort and group events
  const organizedEvents = useMemo(() => {
    // Filter out no-work day events
    const regularEvents = events.filter(e => !e.id?.startsWith('no-work-'));
    
    // Sort events
    const sorted = [...regularEvents].sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    if (groupBy === 'date') {
      // Group by date
      const grouped = {};
      sorted.forEach(event => {
        const dateKey = new Date(event.start).toLocaleDateString('en-US', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(event);
      });
      return grouped;
    } else if (groupBy === 'contractor') {
      // Group by contractor
      const grouped = { 'Unassigned': [] };
      sorted.forEach(event => {
        const contractor = event.contractor_id 
          ? contractors.find(c => c.id === event.contractor_id)
          : null;
        const key = contractor ? contractor.name : 'Unassigned';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(event);
      });
      return grouped;
    } else {
      // Group by task
      const grouped = { 'No Task': [] };
      sorted.forEach(event => {
        const task = event.task_id 
          ? tasks.find(t => t.id === event.task_id)
          : null;
        const key = task ? task.title : 'No Task';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(event);
      });
      return grouped;
    }
  }, [events, groupBy, sortOrder, contractors, tasks]);

  const getEventDuration = (event) => {
    if (!event.end) return '1 day';
    const start = new Date(event.start);
    const end = new Date(event.end);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days === 1 ? '1 day' : `${days} days`;
  };

  const getContractorName = (contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    return contractor ? contractor.name : 'Unassigned';
  };

  const getTaskInfo = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return null;
    return {
      title: task.title,
      completed: task.completed,
      phase: task.phase
    };
  };

  return (
    <div className="event-list-container">
      <div className="event-list-header">
        <h3>📋 Event List ({events.filter(e => !e.id?.startsWith('no-work-')).length})</h3>
        
        <div className="event-list-controls">
          <div className="control-group">
            <label>Group by:</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="date">Date</option>
              <option value="contractor">Contractor</option>
              <option value="task">Task</option>
            </select>
          </div>
          
          <div className="control-group">
            <label>Sort:</label>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="asc">Oldest First</option>
              <option value="desc">Newest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="event-list-content">
        {Object.keys(organizedEvents).length === 0 ? (
          <div className="empty-list">
            <p>No events scheduled</p>
          </div>
        ) : (
          Object.entries(organizedEvents).map(([groupKey, groupEvents]) => (
            <div key={groupKey} className="event-group">
              <div className="group-header">
                <h4>{groupKey}</h4>
                <span className="group-count">{groupEvents.length}</span>
              </div>
              
              <div className="group-events">
                {groupEvents.map(event => {
                  const taskInfo = getTaskInfo(event.task_id);
                  const isCompleted = taskInfo?.completed;
                  
                  return (
                    <div
                      key={event.id}
                      className={`event-list-item ${isCompleted ? 'completed' : ''} ${isAdmin ? 'clickable' : ''}`}
                      style={{ borderLeftColor: event.borderColor || '#4285f4' }}
                      onClick={() => isAdmin && onEventClick && onEventClick(event)}
                    >
                      <div className="event-item-header">
                        <span className="event-title">
                          {event.title}
                          {isCompleted && ' ✓'}
                        </span>
                        <span className="event-duration">
                          {getEventDuration(event)}
                        </span>
                      </div>
                      
                      {groupBy !== 'date' && (
                        <div className="event-date">
                          📅 {new Date(event.start).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      )}
                      
                      {groupBy !== 'contractor' && event.contractor_id && (
                        <div className="event-contractor">
                          👷 {getContractorName(event.contractor_id)}
                        </div>
                      )}
                      
                      {taskInfo && (
                        <div className="event-meta">
                          {taskInfo.phase && (
                            <span className="event-phase">{taskInfo.phase}</span>
                          )}
                        </div>
                      )}
                      
                      {event.notes && (
                        <div className="event-notes">
                          💬 {event.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EventListView;